import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { Field } from '../components/Section';
import { toast } from '../components/Toast';
import { Pill } from '../ui/Pill';
import { Icon, type IconComponent } from '../ui/Icons';
import type { Agent, KbDocument, KbBacklog, KbSourceType, KbDocStatus } from '../lib/types';

type Tab = 'file' | 'url' | 'text';

export const KnowledgeBase = () => {
  const qc = useQueryClient();
  const agentsQ = useQuery<{ agents: Agent[] }>({
    queryKey: ['agents'],
    queryFn: async () => (await api.get('/api/tenant/agents')).data,
  });
  const agent = agentsQ.data?.agents[0];

  const docsQ = useQuery<{ documents: KbDocument[]; backlog: KbBacklog }>({
    queryKey: ['kb', agent?.id],
    queryFn: async () => (await api.get(`/api/tenant/agents/${agent!.id}/kb`)).data,
    enabled: !!agent,
  });

  const deleteMut = useMutation({
    mutationFn: async (docId: string) =>
      (await api.delete(`/api/tenant/agents/${agent!.id}/kb/${docId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kb', agent?.id] });
      toast.success('Document deleted');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const docs = docsQ.data?.documents ?? [];
  const totalChunks = docs.reduce((s, d) => s + d.chunkCount, 0);
  const backlog = docsQ.data?.backlog;
  const embeddingsBlocked = backlog && !backlog.embeddingsConfigured && backlog.pending > 0;

  return (
    <div>
      <PageHeader
        title="Knowledge base"
        subtitle="Documents, pages, and notes your agent draws on when answering."
      />

      <div className="px-10 pb-10 flex flex-col gap-[18px]">
        {/* Metric strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
          <MetricChip label="Sources" value={String(docs.length)} />
          <MetricChip label="Total chunks" value={String(totalChunks)} />
          <MetricChip
            label="Embeddings"
            value={backlog?.embeddingsConfigured ? 'enabled' : 'pending'}
            tone={backlog?.embeddingsConfigured ? 'ok' : 'warn'}
          />
          <MetricChip
            label="Pending"
            value={String(backlog?.pending ?? 0)}
            sub={backlog?.pending ? 'awaiting embedding' : 'all caught up'}
          />
        </div>

        {/* Pending banner */}
        {embeddingsBlocked && (
          <div
            className="rounded-xl px-4 py-3 flex items-center gap-3.5 border"
            style={{
              background: 'linear-gradient(0deg, #FFFBEB, #FFFBEB)',
              borderColor: 'rgba(252,211,77,0.35)',
            }}
          >
            <span className="w-8 h-8 rounded-[10px] bg-amber-100 text-amber-900 flex items-center justify-center shrink-0">
              <Icon.File size={15} />
            </span>
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-amber-900">
                {backlog?.pending} {backlog && backlog.pending === 1 ? 'chunk' : 'chunks'} pending embedding
              </div>
              <div className="text-xs text-amber-900/70 mt-0.5">
                Embeddings aren't yet enabled on this account — your bot can't retrieve from the KB
                until they are. The team will switch them on for you.
              </div>
            </div>
          </div>
        )}

        {/* Upload panel */}
        {agent && (
          <UploadPanel
            agentId={agent.id}
            onIngested={() => qc.invalidateQueries({ queryKey: ['kb', agent.id] })}
          />
        )}

        {/* Sources list */}
        <section className="card overflow-hidden">
          <div className="px-[22px] py-4 border-b border-line flex items-center justify-between">
            <div>
              <div className="text-[13.5px] font-semibold text-ink">Sources</div>
              <div className="text-[11.5px] text-ink-subtle mt-0.5">
                {docs.length} {docs.length === 1 ? 'item' : 'items'} · {totalChunks} chunks indexed
              </div>
            </div>
          </div>

          {!docsQ.data ? (
            <div className="p-10 text-center text-[12.5px] text-ink-subtle">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="p-10 text-center text-[12.5px] text-ink-subtle">
              No documents yet. Add one above.
            </div>
          ) : (
            <ul className="list-none m-0 p-0">
              {docs.map((d, i) => (
                <li
                  key={d.id}
                  className={`px-[22px] py-3.5 flex items-center gap-3.5 ${i > 0 ? 'border-t border-line' : ''}`}
                >
                  <KindBadge kind={d.sourceType} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-ink leading-tight truncate">
                      {d.title}
                    </div>
                    <div className="text-[11.5px] text-ink-subtle mt-1 flex gap-2.5 items-center flex-wrap">
                      {d.sourceRef && (
                        <span
                          className="font-mono text-[11px] truncate max-w-[280px]"
                          title={d.sourceRef}
                        >
                          {d.sourceRef}
                        </span>
                      )}
                      {d.fileSizeBytes != null && (
                        <>
                          <span>·</span>
                          <span>{formatBytes(d.fileSizeBytes)}</span>
                        </>
                      )}
                      <span>·</span>
                      <span>{new Date(d.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="w-40 hidden sm:flex flex-col gap-1 shrink-0">
                    <div className="text-[11px] text-ink-subtle flex justify-between">
                      <span>Chunks</span>
                      <span className="text-ink-soft tabular-nums font-medium">{d.chunkCount}</span>
                    </div>
                    <div className="h-1 bg-bg rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: progressWidth(d.status, d.chunkCount),
                          background: progressColor(d.status),
                        }}
                      />
                    </div>
                  </div>

                  <StatusPill status={d.status} />

                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Delete "${d.title}"? This removes its chunks too.`)) {
                        deleteMut.mutate(d.id);
                      }
                    }}
                    disabled={deleteMut.isPending}
                    aria-label="Delete"
                    className="w-7 h-7 rounded-lg border border-line bg-white text-ink-muted hover:text-rose-700 hover:border-rose-200 transition-colors flex items-center justify-center shrink-0 disabled:opacity-50"
                  >
                    <Icon.Trash size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

// ---- Upload panel ----

const TAB_DEFS: { id: Tab; label: string; icon: IconComponent }[] = [
  { id: 'file', label: 'Upload file', icon: Icon.Upload },
  { id: 'url', label: 'Add URL', icon: Icon.Link },
  { id: 'text', label: 'Paste text', icon: Icon.File },
];

const UploadPanel = ({
  agentId,
  onIngested,
}: {
  agentId: string;
  onIngested: () => void;
}) => {
  const [tab, setTab] = useState<Tab>('file');
  return (
    <section className="card overflow-hidden">
      <div className="px-[22px] pt-[18px] pb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="m-0 text-base font-semibold text-ink tracking-tight">
            Add to knowledge base
          </h2>
          <p className="mt-1 text-[12.5px] text-ink-muted">
            Upload a file, crawl a URL, or paste text directly.
          </p>
        </div>
        <div className="flex gap-1 p-1 bg-[#F2F2F5] rounded-[9px]">
          {TAB_DEFS.map(({ id, label, icon: Ico }) => {
            const on = tab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11.5px] font-medium transition-colors ${
                  on ? 'bg-white text-ink shadow-soft' : 'bg-transparent text-ink-muted hover:text-ink'
                }`}
              >
                <Ico size={12} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-[22px] pb-[22px] pt-2">
        {tab === 'file' && <FileTab agentId={agentId} onIngested={onIngested} />}
        {tab === 'url' && <UrlTab agentId={agentId} onIngested={onIngested} />}
        {tab === 'text' && <TextTab agentId={agentId} onIngested={onIngested} />}
      </div>
    </section>
  );
};

const FileTab = ({
  agentId,
  onIngested,
}: {
  agentId: string;
  onIngested: () => void;
}) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      await api.post(`/api/tenant/agents/${agentId}/kb/file`, fd);
      toast.success(`Ingested "${file.name}"`);
      if (fileInput.current) fileInput.current.value = '';
      onIngested();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const file = fileInput.current?.files?.[0];
    if (file) await upload(file);
  };

  return (
    <form onSubmit={onSubmit}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) {
            if (fileInput.current) {
              const dt = new DataTransfer();
              dt.items.add(file);
              fileInput.current.files = dt.files;
            }
            void upload(file);
          }
        }}
        onClick={() => fileInput.current?.click()}
        className={`rounded-xl p-7 flex flex-col items-center gap-2 cursor-pointer transition-colors ${
          dragOver ? 'border-accent bg-accent-soft/40' : 'border-line-strong'
        }`}
        style={{
          borderStyle: 'dashed',
          borderWidth: 1.5,
          background: dragOver
            ? undefined
            : 'repeating-linear-gradient(45deg, transparent, transparent 10px, #FAFAFB 10px, #FAFAFB 20px)',
        }}
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-tri text-white flex items-center justify-center">
          <Icon.Upload size={20} />
        </div>
        <div className="text-sm font-medium text-ink">
          {busy ? 'Uploading…' : 'Drop a file here, or click to browse'}
        </div>
        <div className="text-[11.5px] text-ink-subtle">
          PDF · DOCX · Markdown · plain text
        </div>
        <input
          ref={fileInput}
          type="file"
          accept=".pdf,.docx,.md,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
          }}
        />
        <button type="button" className="btn-primary mt-1.5" disabled={busy}>
          {busy ? 'Uploading…' : 'Choose file'}
        </button>
      </div>
    </form>
  );
};

const UrlTab = ({
  agentId,
  onIngested,
}: {
  agentId: string;
  onIngested: () => void;
}) => {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/api/tenant/agents/${agentId}/kb`, {
        type: 'url',
        url,
        ...(title ? { title } : {}),
      });
      toast.success('URL ingested');
      setUrl('');
      setTitle('');
      onIngested();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3.5">
      <Field label="URL">
        <input
          type="url"
          className="input"
          placeholder="https://yourdomain.com/about"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          required
        />
      </Field>
      <Field label="Title (optional)" hint="Leave blank to use the page's own title.">
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <div className="flex justify-end">
        <button type="submit" className="btn-primary" disabled={busy || !url}>
          {busy ? 'Fetching…' : 'Add URL'}
        </button>
      </div>
    </form>
  );
};

const TextTab = ({
  agentId,
  onIngested,
}: {
  agentId: string;
  onIngested: () => void;
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/api/tenant/agents/${agentId}/kb`, {
        type: 'text',
        title,
        content,
      });
      toast.success('Note added');
      setTitle('');
      setContent('');
      onIngested();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3.5">
      <Field label="Title">
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Pricing FAQ"
          required
        />
      </Field>
      <Field label="Content">
        <textarea
          className="input min-h-[200px] font-mono text-xs"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          required
        />
      </Field>
      <div className="flex justify-end">
        <button
          type="submit"
          className="btn-primary"
          disabled={busy || !title || content.length < 20}
        >
          {busy ? 'Saving…' : 'Add note'}
        </button>
      </div>
    </form>
  );
};

// ---- bits ----

const MetricChip = ({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'ok' | 'warn';
}) => (
  <div className="bg-white border border-line rounded-xl px-3.5 py-3">
    <div className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-ink-subtle">{label}</div>
    <div className="text-[20px] text-ink mt-1.5 font-medium tracking-tight flex items-baseline gap-1.5">
      {value}
      {tone === 'ok' && <span className="w-[7px] h-[7px] rounded-full bg-emerald-500" />}
      {tone === 'warn' && <span className="w-[7px] h-[7px] rounded-full bg-amber-500" />}
    </div>
    {sub && <div className="text-[11px] text-ink-subtle mt-1">{sub}</div>}
  </div>
);

const KIND_COLORS: Record<KbSourceType, { fg: string; bg: string }> = {
  PDF: { fg: '#DC2626', bg: '#FEE2E2' },
  DOCX: { fg: '#1D4ED8', bg: '#DBEAFE' },
  MARKDOWN: { fg: '#1F2024', bg: '#F2F2F5' },
  TEXT: { fg: '#1F2024', bg: '#F2F2F5' },
  URL: { fg: '#3730A3', bg: '#EEF0FF' },
  FAQ: { fg: '#5B21B6', bg: '#F5F3FF' },
};

const KindBadge = ({ kind }: { kind: KbSourceType }) => {
  const c = KIND_COLORS[kind] ?? { fg: '#6B7280', bg: '#F2F2F5' };
  return (
    <div
      className="w-10 h-10 rounded-[10px] flex items-center justify-center text-[10px] font-semibold tracking-[0.04em] shrink-0"
      style={{ color: c.fg, background: c.bg }}
    >
      {kind}
    </div>
  );
};

const StatusPill = ({ status }: { status: KbDocStatus }) => {
  if (status === 'READY') return <Pill tone="live" dot>indexed</Pill>;
  if (status === 'PROCESSING') return <Pill tone="accent" dot>processing</Pill>;
  if (status === 'FAILED') return <Pill tone="lost" dot>failed</Pill>;
  return <Pill tone="new" dot>queued</Pill>;
};

const progressWidth = (status: KbDocStatus, chunks: number): string => {
  if (status === 'READY') return '100%';
  if (status === 'PROCESSING') return `${Math.min(95, Math.max(15, chunks * 8))}%`;
  if (status === 'FAILED') return '100%';
  return '0%';
};

const progressColor = (status: KbDocStatus): string => {
  if (status === 'PENDING') return '#F59E0B';
  if (status === 'FAILED') return '#EF4444';
  return 'linear-gradient(135deg, #D1D5DB 0%, #A5A8F4 55%, #6366F1 100%)';
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};
